/**
 * Pack Analysis Workbench — Simulator: editor-line model + wire-flatten helper.
 *
 * Spec `pack-analysis-simulator-item-aware-line-entry` (Phase A, task A.1).
 *
 * The Simulator's line editor needs to know a row's *provenance* — typed by
 * hand ("manual"), picked from the item master ("sku"), or a read-only child
 * of an expanded prepack ("prepackChild", Phase C) — to render the right chip
 * and to decide what the row owns. We model that as a *superset* of the wire
 * DTO (`CartonizationLineInput`) with `_`-prefixed FE-only annotations rather
 * than a set of nominally-distinct shapes: the existing Redux reducers keep
 * operating on one array; the `_*` fields are stripped on submit.
 *
 * No wire-contract change: `flattenToWireLines` produces exactly today's
 * `CartonizationLineInput[]`.
 */
import type { CartonizationLineInput } from '@/app/services/api/packAnalysisService';

/** Where an editor row came from — drives the row chip/badge (Req 4.1). */
export type EditorLineOrigin = 'manual' | 'sku' | 'prepackChild';

/** A lightweight reference to a resolved item-master row. */
export interface ResolvedItemRef {
  itemId: number;
  canonicalIdentifier: string;
  itemType: 'SKU' | 'PACK';
  description?: string | null;
  /** The canonical key actually sent to `/cartonization` for this item — its
   *  strictest-scope `CANONICAL_ITEMKEY` alt-key value, else `canonicalIdentifier`. */
  resolvedCanonicalKey: string;
}

/**
 * One row in the Simulator line editor. Extends the wire DTO so it is
 * assignment-compatible everywhere a `CartonizationLineInput[]` is consumed
 * (`SimulatorResultPanel`, `PerPoLineBreakdown` — no prop-type change needed).
 */
export type EditorPoLine = CartonizationLineInput & {
  /** Stable React key + update target. Module counter, NOT `crypto.randomUUID`
   *  (the jest env's randomUUID is flaky here — see `feedback_jest_crypto_randomuuid_pre_existing.md`). */
  _id: string;
  _origin: EditorLineOrigin;
  /** Present iff `_origin === 'sku'` (or a `prepackChild` we managed to resolve). */
  _resolvedItem?: ResolvedItemRef;
  /** Present iff `_origin === 'prepackChild'` — groups consecutive children
   *  under a synthetic parent row (a `PrepackGroupMeta` keyed by this id). */
  _prepackGroupId?: string;
  /** Present iff `_origin === 'prepackChild'` — this sub-item's quantity in ONE
   *  prepack instance. `orderQuantity` for the line = `_perPrepackQty × prepackCount`. */
  _perPrepackQty?: number;
};

/** Sidecar metadata for an expanded prepack group (lives in the slice, keyed by
 *  `_prepackGroupId`; the children themselves live inline in `poLines`). */
export interface PrepackGroupMeta {
  prepackCode: string;
  /** The `PACK` item the operator picked; kept for reference (re-expansion etc.). */
  packItemId: number;
  description?: string | null;
  /** The one operator-editable field — how many of this prepack are on the PO. */
  prepackCount: number;
  /** Read-only display = `Σ subItems[].quantity` (units in one prepack instance).
   *  Informational — the authoritative pack factor is server-derived (Open Q1). */
  unitsPerPrepack: number;
}

/** One component sub-item of a prepack, as returned by `GET /edi/items/{id}/pack-composition`. */
export interface PrepackCompositionSubItem {
  /** The canonical `item_key` the engine recorded in the PackMaster's `composition_json`. */
  itemKey: string;
  itemId?: number | null;
  canonicalIdentifier?: string | null;
  description?: string | null;
  quantity: number;
  uom: string;
}

// ── id minting ────────────────────────────────────────────────────────────

let _counter = 0;
/** A process-unique editor-line id. Stable enough for React keys; never sent
 *  on the wire. Not cryptographic by design (jest randomUUID flakiness). */
export function nextEditorLineId(prefix = 'el'): string {
  _counter += 1;
  return `${prefix}_${_counter}_${Date.now().toString(36)}`;
}

// ── factories ─────────────────────────────────────────────────────────────

/** A fresh, blank manual line (the "+ Add line" default — replaces `makeEmptyPoLine`). */
export function makeEmptyManualLine(): EditorPoLine {
  return {
    _id: nextEditorLineId(),
    _origin: 'manual',
    itemKey: '',
    orderQuantity: 0,
    uom: 'EA',
  };
}

/** A line populated from a picked item-master SKU/PACK row. `itemKey` is set to
 *  the resolved canonical key; the `_resolvedItem` drives the row chip. */
export function makeResolvedSkuLine(ref: ResolvedItemRef, orderQuantity = 0, uom = 'EA'): EditorPoLine {
  return {
    _id: nextEditorLineId(),
    _origin: 'sku',
    _resolvedItem: ref,
    itemKey: ref.resolvedCanonicalKey,
    orderQuantity,
    uom: (uom || 'EA').toUpperCase(),
  };
}

/** Ensure an arbitrary `CartonizationLineInput` (e.g. from PackMaster-composition
 *  seeding or sample data) becomes a manual `EditorPoLine`. Idempotent if it
 *  already carries an `_id`. */
export function toEditorLine(line: CartonizationLineInput | EditorPoLine): EditorPoLine {
  if ('_id' in line && (line as EditorPoLine)._id) return line as EditorPoLine;
  return { ...(line as CartonizationLineInput), _id: nextEditorLineId(), _origin: 'manual' };
}

/** A read-only prepack sub-item line — already in wire shape (`packType:'P'`,
 *  `packGroupKey`, `packFactor:null` ⇒ server-derived per Open Q1). `orderQuantity`
 *  is `quantity × prepackCount`; the reducer rewrites it when the count changes. */
export function makePrepackChildLine(
  sub: PrepackCompositionSubItem,
  groupId: string,
  prepackCode: string,
  prepackCount: number
): EditorPoLine {
  const q = Number(sub.quantity) || 0;
  const resolved: ResolvedItemRef | undefined =
    sub.itemId != null && sub.canonicalIdentifier != null
      ? { itemId: sub.itemId, canonicalIdentifier: sub.canonicalIdentifier, itemType: 'SKU', description: sub.description ?? null, resolvedCanonicalKey: sub.itemKey }
      : undefined;
  return {
    _id: nextEditorLineId(),
    _origin: 'prepackChild',
    _prepackGroupId: groupId,
    _perPrepackQty: q,
    _resolvedItem: resolved,
    itemKey: sub.itemKey,
    orderQuantity: q * Math.max(1, prepackCount),
    uom: (sub.uom || 'EA').toUpperCase(),
    packType: 'P',
    packGroupKey: prepackCode,
    packFactor: null,
  };
}

// ── flatten to the wire DTO ───────────────────────────────────────────────

/** Strip every `_`-prefixed FE-only field, trim `itemKey`, uppercase `uom`. */
export function toWireLine(line: EditorPoLine): CartonizationLineInput {
  const wire: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(line)) {
    if (k.startsWith('_')) continue;
    wire[k] = v;
  }
  wire.itemKey = String(line.itemKey ?? '').trim();
  wire.uom = String(line.uom || 'EA').toUpperCase();
  return wire as unknown as CartonizationLineInput;
}

/** True iff this row is "complete enough" to send: a non-blank identifier and a
 *  positive order quantity. (Blank rows the operator added but didn't fill are
 *  silently dropped — current behaviour.) */
export function isSendableLine(line: EditorPoLine): boolean {
  return String(line.itemKey ?? '').trim() !== '' && (Number(line.orderQuantity) || 0) > 0;
}

/** The Simulator's editor state → the cartonization request's `lines[]`. */
export function flattenToWireLines(lines: ReadonlyArray<EditorPoLine>): CartonizationLineInput[] {
  return lines.filter(isSendableLine).map(toWireLine);
}
